import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Camera, Loader2, CheckCircle2, Circle, Download, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, unwrap, API_BASE, getToken } from "@/lib/api";

export default function AdminWorkPhotos({ bookingId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {photo, url}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/bookings/${bookingId}/work-photos`);
      setPhotos(unwrap(r) || []);
    } catch {
      /* leave as is */
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = { before: [], after: [] };
    (photos || []).forEach((p) => { if (g[p.phase]) g[p.phase].push(p); });
    return g;
  }, [photos]);

  const toggleApprove = async (photo) => {
    const next = !photo.approved_for_marketing;
    try {
      const r = await api.patch(`/admin/bookings/${bookingId}/work-photos/${photo.id}`, { approved_for_marketing: next });
      const fresh = unwrap(r);
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, ...fresh } : p)));
      toast.success(next ? "Approved for marketing" : "Removed from marketing");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Update failed");
    }
  };

  const remove = async (photo) => {
    if (!window.confirm("Delete this photo permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${bookingId}/work-photos/${photo.id}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
      toast.success("Photo deleted");
      if (lightbox?.photo?.id === photo.id) setLightbox(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Delete failed");
    }
  };

  const openLightbox = async (photo) => {
    try {
      const r = await fetch(`${API_BASE}/admin/bookings/${bookingId}/work-photos/${photo.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error("fetch");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      setLightbox({ photo, url, blob });
    } catch {
      toast.error("Could not load photo");
    }
  };

  const closeLightbox = () => {
    if (lightbox?.url) URL.revokeObjectURL(lightbox.url);
    setLightbox(null);
  };

  const download = (photo, url) => {
    const a = document.createElement("a");
    a.href = url;
    const ext = (photo.content_type || "image/jpeg").split("/").pop().replace("jpeg", "jpg");
    a.download = `booking-${bookingId.slice(-6)}__${photo.phase}__${(photo.room_label || "photo").replace(/\s+/g, "-")}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  if (loading && photos.length === 0) {
    return (
      <Card className="rounded-2xl p-6 border border-[#E5E7EB]">
        <h3 className="font-display font-bold mb-4 flex items-center gap-2">
          <Camera className="w-4 h-4 text-[#0EA5A4]" /> Work photos (before / after)
        </h3>
        <div className="text-sm text-[#9CA3AF]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      </Card>
    );
  }

  const hasAny = photos.length > 0;

  return (
    <Card className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid="admin-work-photos">
      <h3 className="font-display font-bold mb-4 flex items-center gap-2">
        <Camera className="w-4 h-4 text-[#0EA5A4]" /> Work photos (before / after)
      </h3>

      {!hasAny ? (
        <p className="text-sm text-[#9CA3AF]">The cleaner has not uploaded any before/after photos yet.</p>
      ) : (
        ["before", "after"].map((phase) => (
          <div key={phase} className="mb-5 last:mb-0">
            <div className="text-xs uppercase tracking-wide text-[#9CA3AF] font-semibold mb-2 capitalize">
              {phase} <span className="text-[#4B5563] normal-case">({grouped[phase].length})</span>
            </div>
            {grouped[phase].length === 0 ? (
              <div className="text-xs text-[#9CA3AF] italic">No {phase} photos</div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid={`admin-${phase}-grid`}>
                {grouped[phase].map((p) => (
                  <AdminThumb
                    key={p.id}
                    photo={p}
                    bookingId={bookingId}
                    onOpen={() => openLightbox(p)}
                    onToggleApprove={() => toggleApprove(p)}
                    onDelete={() => remove(p)}
                  />
                ))}
              </div>
            )}
          </div>
        ))
      )}

      {lightbox && (
        <Lightbox
          lightbox={lightbox}
          onClose={closeLightbox}
          onDownload={() => download(lightbox.photo, lightbox.url)}
          onToggleApprove={() => toggleApprove(lightbox.photo)}
          onDelete={() => remove(lightbox.photo)}
        />
      )}
    </Card>
  );
}

function AdminThumb({ photo, bookingId, onOpen, onToggleApprove, onDelete }) {
  const [url, setUrl] = useState("");

  useEffect(() => {
    let active = true;
    let blobUrl = "";
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/admin/bookings/${bookingId}/work-photos/${photo.id}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) return;
        const blob = await r.blob();
        blobUrl = URL.createObjectURL(blob);
        if (active) setUrl(blobUrl);
      } catch { /* keep empty */ }
    })();
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [bookingId, photo.id]);

  return (
    <div className="relative group" data-testid="admin-work-photo-thumb">
      <button
        onClick={onOpen}
        className="w-full aspect-square rounded-lg overflow-hidden bg-[#F5F5F4] border border-[#E5E7EB] grid place-items-center hover:ring-2 hover:ring-[#0EA5A4] transition"
      >
        {url ? (
          <img src={url} alt={photo.room_label || "work photo"} className="w-full h-full object-cover" />
        ) : (
          <Loader2 className="w-4 h-4 animate-spin text-[#9CA3AF]" />
        )}
      </button>
      <div className="flex items-center justify-between mt-1 gap-1">
        <span className="text-[10px] font-mono text-[#4B5563] truncate">{photo.room_label || "—"}</span>
        <button
          onClick={onToggleApprove}
          title={photo.approved_for_marketing ? "Approved — click to remove" : "Approve for marketing"}
          data-testid={`approve-toggle-${photo.id}`}
          className={`shrink-0 rounded-full p-0.5 ${photo.approved_for_marketing ? "text-emerald-600" : "text-[#9CA3AF] hover:text-emerald-600"}`}
        >
          {photo.approved_for_marketing ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
        </button>
      </div>
      {photo.approved_for_marketing && (
        <Badge className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] px-1.5 py-0.5" data-testid="approved-badge">✓</Badge>
      )}
    </div>
  );
}

function Lightbox({ lightbox, onClose, onDownload, onToggleApprove, onDelete }) {
  const { photo, url } = lightbox;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 grid place-items-center p-4"
      onClick={onClose}
      data-testid="work-photo-lightbox"
    >
      <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white grid place-items-center shadow" aria-label="Close" data-testid="lightbox-close">
          <X className="w-4 h-4" />
        </button>
        <img src={url} alt={photo.room_label || "work photo"} className="w-full max-h-[75vh] object-contain rounded-xl bg-black" />
        <div className="mt-3 bg-white rounded-xl p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold capitalize">{photo.phase} · {photo.room_label || "—"}</div>
            <div className="text-xs text-[#9CA3AF] truncate">by {photo.uploaded_by} · {new Date(photo.uploaded_at).toLocaleString()}</div>
          </div>
          <Button onClick={onToggleApprove} variant="outline" className="rounded-xl" data-testid="lightbox-approve-btn">
            {photo.approved_for_marketing ? (
              <><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Approved</>
            ) : (
              <><Circle className="w-4 h-4 mr-2" /> Approve for marketing</>
            )}
          </Button>
          <Button onClick={onDownload} variant="outline" className="rounded-xl" data-testid="lightbox-download-btn">
            <Download className="w-4 h-4 mr-2" /> Download
          </Button>
          <Button onClick={onDelete} variant="outline" className="rounded-xl text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="lightbox-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
